import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _findIndex from 'lodash/findIndex';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
    selectPreferences,
    selectFeatureFlags,
    selectInteractiveVizNavCacheMins,
} from '../../selectors/ContextSelector';
import withReducerSaga from '../../hoc/ReducerRegistry/withReducerSaga';
// Custom Components for the main Components
import LandingModal from './components/LandingModal/LandingModal';
import SizeStyleSelector from './components/SizeStyleSelector/SizeStyleSelector';
import InteractiveVizNav from './components/InteractiveVizNav/InteractiveVizNav';
import DetailShort from './components/DetailShort/DetailShort';
import AnalyticsAction from '../../actions/AnalyticsAction';
import { setFilterSelection } from '../../actions/SelectionFilterAction';
import { setClearAllFilter } from '../../actions/ClearAllFilterAction';
import RichText from '../RichText/RichText';
import { getMediaTypeForDeviceType } from '../../utils';
import {
    getInteractiveVizNavFromSession,
    setInteractiveVizNavInSession,
    isNotIrisInTool,
    validQueryString,
    getExcludeFiltersList,
    extractTextFromHtml,
} from '../../helpers/Utils/interactiveVizNav';
import SelectionFilterReducer from '../../reducers/SelectionFilterReducer';
import ClearAllFilterReducer from '../../reducers/ClearAllFilterReducer';
import * as styles from './FitFinder.css';

const cx = classNames.bind(styles);

export class FitFinder extends PureComponent {
    static defaultProps = {
        deviceType: { isDesktop: false, isMobile: true, isTablet: false },
        interactiveVizNav: {},
        actions: {},
        selectedFilters: [],
        preferences: {},
        featureFlags: {},
        selectionFilter: {},
        clearAllFilter: {},
    };

    static propTypes = {
        deviceType: PropTypes.object,
        interactiveVizNav: PropTypes.object,
        actions: PropTypes.object.isRequired,
        selectedFilters: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        preferences: PropTypes.object,
        featureFlags: PropTypes.objectOf(PropTypes.bool),
        selectionFilter: PropTypes.object,
        cacheMins: PropTypes.number.isRequired,
        clearAllFilter: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            navLinks: [],
            inspirationDetails: {},
            navigationIndex: _get(this.props.interactiveVizNav, 'defaultNavigationIndex', 0),
            selectedNavigationIndex: _get(
                this.props.interactiveVizNav,
                'defaultNavigationIndex',
                0
            ),
        };
    }

    /*
     * prefetch images for interactive viz nav
     * fit finder is being called everytime on server side
     * so we are good to prefetch images on mount
     */
    componentDidMount() {
        const { interactiveVizNav, deviceType, preferences, cacheMins } = this.props;
        if (!isEmpty(interactiveVizNav)) {
            const navigations = _get(interactiveVizNav, 'navigations', [{}]);
            this.prefetchAllImages(navigations, deviceType);
            const excludedFiltersList = getExcludeFiltersList(preferences);
            if (!validQueryString(window.location.href, excludedFiltersList)) {
                const programId = _get(interactiveVizNav, 'programId', '');
                setInteractiveVizNavInSession(programId, 'heading', '', cacheMins);
            }
        }
        this.setNavigationIndex();
    }

    componentWillReceiveProps(nextProps) {
        const {
            interactiveVizNav,
            deviceType,
            clearAllFilter: { isClearAllFilterApplied = false },
        } = nextProps;
        if (!isEqual(interactiveVizNav, this.props.interactiveVizNav)) {
            const navigations = _get(interactiveVizNav, 'navigations', [{}]);
            this.prefetchAllImages(navigations, deviceType);
        }
        if (
            !isEqual(
                _get(interactiveVizNav, 'defaultNavigationIndex', 0),
                _get(this.props.interactiveVizNav, 'defaultNavigationIndex', 0)
            )
        ) {
            this.setState({
                navigationIndex: _get(interactiveVizNav, 'defaultNavigationIndex', 0),
            });
            this.setNavigationIndex(nextProps);
        }
        if (isClearAllFilterApplied) {
            const defaultIndex = _get(interactiveVizNav, 'defaultNavigationIndex', 0);
            const programId = _get(interactiveVizNav, 'programId', '');
            setInteractiveVizNavInSession(programId, 'navigationIndex', defaultIndex);
            this.setNavigationIndex(nextProps);
        }
    }

    onStyleChange = (navItem, selectedNavigationIndex) => {
        const {
            interactiveVizNav,
            actions: {
                fitFinderModalAndPersonaAnalytics,
                setFilterSelection: filterSelection,
                setClearAllFilter: clearAllFilterSelection,
            },
            cacheMins,
        } = this.props;
        const navigations = _get(interactiveVizNav, 'navigations', {});
        const programId = _get(interactiveVizNav, 'programId', '');
        const item = _find(navigations, (n) => n.navItem === navItem);
        const itemIndex = _findIndex(navigations, (n) => n.navItem === navItem);
        const popup = _get(interactiveVizNav, 'popup', {});
        const inspirationDetailsWithoutSelection = { ...popup };
        this.setState({
            navLinks: item ? item.navLinks : [],
            navigationIndex: itemIndex,
            inspirationDetails: inspirationDetailsWithoutSelection,
            selectedNavigationIndex,
        });
        setInteractiveVizNavInSession(programId, 'heading', '', cacheMins);
        setInteractiveVizNavInSession(programId, 'navigationIndex', itemIndex, cacheMins);
        fitFinderModalAndPersonaAnalytics();
        filterSelection({ isBottomToTopFilterSelection: false });
        clearAllFilterSelection({ isClearAllFilterApplied: false });
    };

    onInteractiveVizNaveSizeSelectionChange = ({ heading, callAnalytics }) => {
        const {
            interactiveVizNav,
            actions: {
                promotedFilterChange,
                setFilterSelection: filterSelection,
                setClearAllFilter: clearAllFilterSelection,
            },
        } = this.props;
        const navigations = _get(interactiveVizNav, 'navigations', {});
        const programId = _get(interactiveVizNav, 'programId', '');
        this.setNavigationIndex(); // this is added to handle the case where if they add styles under different program ids
        const { navigationIndex } = getInteractiveVizNavFromSession(programId);
        const selectedNavItem = _get(navigations[navigationIndex], 'navItem', '');
        const item = _find(navigations, (n) => n.navItem === selectedNavItem);
        const navLinkItem = _find(
            item.navLinks,
            (i) => extractTextFromHtml(i.heading) === extractTextFromHtml(heading)
        );
        this.setState({
            inspirationDetails: (navLinkItem || {}).inspirationDetails,
        });
        callAnalytics &&
            promotedFilterChange({
                value: 'interactivevisnav',
                heading: extractTextFromHtml(heading),
            });
        callAnalytics && filterSelection({ isBottomToTopFilterSelection: false });
        callAnalytics && clearAllFilterSelection({ isClearAllFilterApplied: false });
    };

    setNavigationIndex = (nextProps = this.props) => {
        const programId = _get(this.props.interactiveVizNav, 'programId', '');
        const defaultNavigationIndex = _get(
            this.props.interactiveVizNav,
            'defaultNavigationIndex',
            0
        );
        const { navigationIndex: sessionNavigationIndex } = getInteractiveVizNavFromSession(
            programId
        );
        // sessionNavigationIndex >= 0 this was added since javascript is considering 0 as undefined (if sessionNavigationIndex is zero its returning false)
        const navigationIndex =
            sessionNavigationIndex >= 0 ? sessionNavigationIndex : defaultNavigationIndex;
        setInteractiveVizNavInSession(
            programId,
            'navigationIndex',
            navigationIndex,
            this.props.cacheMins
        );
        if (!isNotIrisInTool()) {
            this.setState({
                navigationIndex: _get(nextProps.interactiveVizNav, 'defaultNavigationIndex', 0),
            });
        } else {
            this.setState({ navigationIndex, selectedNavigationIndex: navigationIndex });
        }
    };

    prefetchAllImages(navigations, deviceType) {
        navigations &&
            navigations.forEach((element) => {
                const navLinks = _get(element, 'navLinks', []);
                navLinks.forEach((navLink) => {
                    const media = getMediaTypeForDeviceType(deviceType, navLink);
                    const url = _get(media, 'url', '');
                    if (url) {
                        new Image().src = url;
                    }
                });
            });
    }

    render() {
        const {
            deviceType,
            interactiveVizNav,
            selectedFilters,
            featureFlags,
            preferences,
            selectionFilter: { isBottomToTopFilterSelection = false },
            clearAllFilter: { isClearAllFilterApplied = false },
            cacheMins,
        } = this.props;
        const { inspirationDetails, navigationIndex, selectedNavigationIndex } = this.state;
        const isDisplayFitFinder = _get(featureFlags, 'disableFitFinder', false);
        const isDisableFitFinderLeftNavFilters = _get(
            featureFlags,
            'disableFitFinderLeftNavFilters',
            false
        );
        const excludedFiltersList = getExcludeFiltersList(preferences);
        let selectedInspirationDetails = inspirationDetails;
        if (!isEmpty(interactiveVizNav) && !isDisplayFitFinder) {
            const navigations = _get(interactiveVizNav, 'navigations', {});
            const popup = _get(interactiveVizNav, 'popup', {});
            const inspirationDetailsWithoutSelection = {
                ...popup,
            };
            let renderTitleStyles = {};
            if (interactiveVizNav.backgroundMedia) {
                renderTitleStyles = {
                    backgroundImage: `url('${interactiveVizNav.backgroundMedia.url}')`,
                };
            } else {
                renderTitleStyles = {
                    backgroundColor: interactiveVizNav.backgroundColor,
                };
            }
            const programId = _get(interactiveVizNav, 'programId', '');
            if (isEmpty(this.state.inspirationDetails)) {
                this.setState({
                    inspirationDetails: navigations[0].navLinks.inspirationDetails,
                });
            }
            const selectedNavLinks =
                navigations[navigationIndex] && navigations[navigationIndex].navLinks;
            const { heading } = getInteractiveVizNavFromSession(programId);
            const navLinkItem = _find(
                selectedNavLinks,
                (i) => extractTextFromHtml(i.heading) === extractTextFromHtml(heading)
            );
            const { inspirationDetails: sessionInspirationalDetails } = navLinkItem || {};
            if (!isEmpty(heading)) {
                selectedInspirationDetails = isEmpty(sessionInspirationalDetails)
                    ? {}
                    : sessionInspirationalDetails;
            } else {
                selectedInspirationDetails =
                    deviceType.isDesktop && popup ? inspirationDetailsWithoutSelection : null;
            }
            const customClass = deviceType.isTablet ? 'tabletCustomClass' : '';
            const modalCustomClass = deviceType.isMobile ? 'mobileCustomClass' : 'modalBlock';
            return (
                <>
                    {/* Modal Box Block */}
                    {isNotIrisInTool() && (
                        <LandingModal
                            deviceType={deviceType}
                            customClass={modalCustomClass}
                            popup={popup}
                            programId={programId}
                            cacheMins={cacheMins}
                        />
                    )}
                    {/* Modal Box Block Ends */}
                    {/* fitFinder Content Container */}
                    <div className={cx('fitFinderContentContainer', customClass)}>
                        {/* Select box, Info Content and Slider Content Info Ends */}
                        <div className={cx('renderSelectBoxTitleDetailContainer')}>
                            {/* Title, Select Box Block and slider */}
                            <div className={cx('renderSelectBoxTitleDetailBlock')}>
                                {/* Title and Select Box Block */}
                                <div
                                    className={cx('renderTitleSelectBlockContainer')}
                                    style={renderTitleStyles}
                                >
                                    {/* Select Block Title Block */}
                                    <div className={cx('renderContentTitleBlock')}>
                                        <h3
                                            data-automation-id="select-box-title"
                                            className={cx('renderContentMainTitle')}
                                        >
                                            <RichText text={interactiveVizNav.headline} />
                                        </h3>
                                        <h4
                                            data-automation-id="select-box-desc"
                                            className={cx('renderContentDesc')}
                                        >
                                            <RichText text={interactiveVizNav.subHeadline} />
                                        </h4>
                                    </div>
                                    {/* Select Block Title Block Ends */}
                                    {/* Select Box Component */}
                                    <SizeStyleSelector
                                        navigations={navigations}
                                        onStyleChange={this.onStyleChange}
                                        navigationIndex={navigationIndex}
                                        customBlockClass={cx('renderSelectBlock')}
                                        customClass={cx('renderSelect')}
                                        selectedNavigationIndex={selectedNavigationIndex}
                                    />
                                    {/* Select Box Component Ends */}
                                </div>
                                {/*
                                    Content Info for Desktop
                                    **** Have to change/control with DeviceType to Add/Remove ****
                                */}
                                {selectedInspirationDetails &&
                                deviceType.isDesktop &&
                                !__SERVER__ ? (
                                    <DetailShort
                                        preferences={preferences}
                                        customClass={customClass}
                                        inspirationDetails={selectedInspirationDetails}
                                    />
                                ) : null}
                                {/* Content Info  for Desktop Ends */}
                            </div>
                            {/* Title, Select Box Block and slider Ends */}
                            {/* Slider Block */}
                            <div className={cx('renderContentSliderContainer')}>
                                <section className={cx('sliderBlock')}>
                                    {selectedNavLinks ? (
                                        <InteractiveVizNav
                                            cacheMins={cacheMins}
                                            customClass={customClass}
                                            isBottomToTopFilterSelection={
                                                isBottomToTopFilterSelection
                                            }
                                            isClearAllFilterApplied={isClearAllFilterApplied}
                                            deviceType={deviceType}
                                            vizNav={selectedNavLinks}
                                            softRefresh={_get(
                                                interactiveVizNav,
                                                'softRefresh',
                                                true
                                            )}
                                            programId={programId}
                                            preferences={preferences}
                                            selectedFilters={selectedFilters}
                                            disableFitFinderLeftNavFilters={
                                                isDisableFitFinderLeftNavFilters
                                            }
                                            excludeQueryString={excludedFiltersList}
                                            onInteractiveVizNaveSizeSelectionChange={
                                                this.onInteractiveVizNaveSizeSelectionChange
                                            }
                                        />
                                    ) : null}
                                </section>
                            </div>
                            {/* Slider Block Ends */}
                            {/* Title, Select Box Block and slider Ends */}
                        </div>
                        {/* Select box, Info Content and Slider Content Info Ends */}
                        {!__SERVER__ && selectedInspirationDetails && !deviceType.isDesktop ? (
                            <DetailShort
                                preferences={preferences}
                                customClass={customClass}
                                inspirationDetails={selectedInspirationDetails}
                            />
                        ) : null}
                    </div>
                    {/* fitFinder Content Container Ends */}
                </>
            );
        }
        return null;
    }
}

export const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            promotedFilterChange: AnalyticsAction.promotedFilterChange,
            fitFinderModalAndPersonaAnalytics: AnalyticsAction.fitFinderModalAndPersonaAnalytics,
            setFilterSelection,
            setClearAllFilter,
        },
        dispatch
    ),
});

const mapStateToProps = (store) => ({
    selectedFilters: store.selectedFilters,
    featureFlags: selectFeatureFlags(store),
    preferences: selectPreferences(store),
    selectionFilter: store.selectionFilter,
    cacheMins: selectInteractiveVizNavCacheMins(store),
    clearAllFilter: store.clearAllFilter,
});

export default compose(
    withReducerSaga([SelectionFilterReducer, ClearAllFilterReducer]),
    connect(mapStateToProps, mapDispatchToProps)
)(FitFinder);
